import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useDocumentCapturePhaseListener } from "Hooks/useDocumentCapturePhaseListener";
import { useWindowResizeListener } from "Hooks/useWindowResizeListener";
import classNames from "classnames";
import IntlMessages from "Util/IntlMessages";
import { NavItem, UncontrolledCollapse } from "reactstrap";
import { NavLink, useLocation, matchPath } from "react-router-dom";
import {
  DASHBOARD_SUITE_ORDER,
  getSuiteNav,
  suiteAbsolutePath,
  type SuiteNavItem
} from "../../routes/main/layout/_suite/suiteNav";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";

import { useDispatch } from "react-redux";
import { useAppSelector } from "Redux/hooks";
import { setContainerClassnames } from "Redux/actions";

const SCROLL_OPTIONS = { suppressScrollX: true, wheelPropagation: false };

function routeActive(pathname: string, pattern: string, end = false) {
  return matchPath({ path: pattern, end }, pathname) != null;
}

function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const dispatch = useDispatch();
  const containerClassnames = useAppSelector(s => s.menu.containerClassnames);
  const subHiddenBreakpoint = useAppSelector(s => s.menu.subHiddenBreakpoint);
  const menuHiddenBreakpoint = useAppSelector(s => s.menu.menuHiddenBreakpoint);
  const menuClickCount = useAppSelector(s => s.menu.menuClickCount);

  const sidebarRootRef = useRef<HTMLDivElement | null>(null);
  const containerClassnamesRef = useRef(containerClassnames);
  containerClassnamesRef.current = containerClassnames;

  const getMenuClassesForResize = useCallback(
    (classes: string) => {
      let nextClasses = classes.split(" ").filter((x: string) => x !== "");
      const windowWidth = window.innerWidth;
      if (windowWidth < menuHiddenBreakpoint) {
        nextClasses.push("menu-mobile");
      } else if (windowWidth < subHiddenBreakpoint) {
        nextClasses = nextClasses.filter((x: string) => x !== "menu-mobile");
        if (
          nextClasses.includes("menu-default") &&
          !nextClasses.includes("menu-sub-hidden")
        ) {
          nextClasses.push("menu-sub-hidden");
        }
      }
      return nextClasses;
    },
    [menuHiddenBreakpoint, subHiddenBreakpoint]
  );

  const toggle = useCallback(() => {
    const currentClasses = containerClassnames
      ? containerClassnames.split(" ").filter(x => x !== "")
      : [];

    if (currentClasses.includes("menu-sub-hidden") && menuClickCount === 2) {
      dispatch(setContainerClassnames(2, containerClassnames));
    } else if (
      currentClasses.includes("menu-hidden") ||
      currentClasses.includes("menu-mobile")
    ) {
      dispatch(setContainerClassnames(0, containerClassnames));
    }
  }, [containerClassnames, dispatch, menuClickCount]);

  const handleDocumentClick = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const container = sidebarRootRef.current;
      if (!container) {
        return;
      }
      const t = e.target;
      const isMenuClick =
        t instanceof Element &&
        Boolean(
          t.closest(".menu-button, .menu-button-mobile, .mobile-menu-button")
        );
      if (
        t instanceof Node &&
        (container.contains(t) || container === t || isMenuClick)
      ) {
        return;
      }
      toggle();
    },
    [toggle]
  );

  const handleWindowResize = useCallback(
    (event: UIEvent) => {
      if (event && !event.isTrusted) {
        return;
      }
      const nextClasses = getMenuClassesForResize(
        containerClassnamesRef.current
      );
      dispatch(setContainerClassnames(0, nextClasses.join(" ")));
    },
    [dispatch, getMenuClassesForResize]
  );

  useDocumentCapturePhaseListener(true, handleDocumentClick);

  useWindowResizeListener(handleWindowResize, true);

  useLayoutEffect(() => {
    handleWindowResize({ isTrusted: true } as unknown as UIEvent);
  }, [handleWindowResize]);

  const pathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathnameRef.current !== pathname) {
      pathnameRef.current = pathname;
      toggle();
      window.scrollTo(0, 0);
    }
  }, [pathname, toggle]);

  return (
    <div className="sidebar" ref={sidebarRootRef}>
      <div className="main-menu">
        <PerfectScrollbar option={SCROLL_OPTIONS}>
          <div className="scroll">
            <nav className="ml-4 mt-5">
              <div className="offcanvas-main-links">
                <NavItem id="dashboard" className="mt-2">
                  <NavLink className="sub-menu-links" to="/app/main/layout/main">
                    <i className="fab fa-dashcube mr-2" />{" "}
                    <IntlMessages id="menu.main" />
                    <div className="pr-3 float-right">
                      <i className="fa fa-angle-down color-light" />
                    </div>
                  </NavLink>
                </NavItem>
              </div>
              <UncontrolledCollapse
                toggler="#dashboard"
                className="list-style-none sub-menu-collapse"
              >
                {DASHBOARD_SUITE_ORDER.map(meta => {
                  const navId = `suite-dash-${meta.key.replace(/[^a-z0-9-]/gi, "")}`;
                  const items = getSuiteNav(meta.key) || [];
                  const suiteBase = suiteAbsolutePath(meta.key, { to: "." });
                  return (
                    <React.Fragment key={meta.key}>
                      <div className="offcanvas-main-links">
                        <NavItem id={navId} className="mt-1 ms-1">
                          <NavLink className="sub-menu-links" to={suiteBase}>
                            <i className={meta.iconClassName} />{" "}
                            <IntlMessages id={meta.labelId} />
                            <div className="pr-3 float-right">
                              <i className="fa fa-angle-down color-light" />
                            </div>
                          </NavLink>
                        </NavItem>
                      </div>
                      <UncontrolledCollapse
                        toggler={`#${navId}`}
                        className="list-style-none sub-menu-collapse"
                      >
                        {items.map((item: SuiteNavItem) => {
                          const subPath = suiteAbsolutePath(meta.key, item);
                          const active = routeActive(
                            pathname,
                            subPath,
                            Boolean(item.end)
                          );
                          return (
                            <NavItem
                              key={`${meta.key}-${item.to}`}
                              className={classNames("sub-menu-cus ms-3", {
                                active
                              })}
                            >
                              <NavLink
                                to={subPath}
                                end={Boolean(item.end)}
                              >
                                <IntlMessages id={item.labelId} />
                              </NavLink>
                            </NavItem>
                          );
                        })}
                      </UncontrolledCollapse>
                    </React.Fragment>
                  );
                })}
              </UncontrolledCollapse>
              <div className="offcanvas-main-links">
                <NavItem id="shortcodes" className="mt-3">
                  <NavLink className="sub-menu-links" to="/app/main/layout">
                    <i className="fab fa-free-code-camp mr-1" />{" "}
                    <IntlMessages id="menu.shortcodes" />
                    <div className="pr-3 float-right">
                      <i className="fa fa-angle-down color-light" />
                    </div>
                  </NavLink>
                </NavItem>
              </div>
              <UncontrolledCollapse
                toggler="#shortcodes"
                className="list-style-none sub-menu-collapse"
              >
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/buttons", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/buttons">
                    <i className="far fa-heart font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.buttons" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/social", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/social">
                    <i className="far fa-comments mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.social" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/badges", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/badges">
                    <i className="far fa-bell mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.badges" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/tabs", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/tabs">
                    <i className="far fa-window-maximize mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.tabs" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/progress", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/progress">
                    <i className="fab fa-gitter mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.progress" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/timeline", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/timeline">
                    <i className="far fa-edit mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.timeline" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/modals", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/modals">
                    <i className="far fa-clone mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.modals" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/tooltip", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/tooltip">
                    <i className="far fa-comment-alt mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.tooltip" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/popover", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/popover">
                    <i className="far fa-caret-square-up mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.popover" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/carousels", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/carousels">
                    <i className="fa fa-arrows-alt-h mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.carousel" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/columns", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/columns">
                    <i className="fa fa-chalkboard mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.columns" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(
                      pathname,
                      "/app/shortcodes/typography",
                      true
                    )
                  })}
                >
                  <NavLink to="/app/shortcodes/typography">
                    <i className="fa fa-heading mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.typography" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(
                      pathname,
                      "/app/shortcodes/pagination",
                      true
                    )
                  })}
                >
                  <NavLink to="/app/shortcodes/pagination">
                    <i className="fa fa-angle-double-right mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.pagination" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/player", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/player">
                    <i className="far fa-play-circle mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.player" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/alerts", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/alerts">
                    <i className="far fa-bell mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.alerts" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/forms", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/forms">
                    <i className="far fa-list-alt mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.forms" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(
                      pathname,
                      "/app/shortcodes/forms-inputs",
                      true
                    )
                  })}
                >
                  <NavLink to="/app/shortcodes/forms-inputs">
                    <i className="far fa-list-alt mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.forms-input" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/shortcodes/tables", true)
                  })}
                >
                  <NavLink to="/app/shortcodes/tables">
                    <i className="fa fa-table mr-1 font-color-1" />{" "}
                    <IntlMessages id="menu.tables" />
                  </NavLink>
                </NavItem>
              </UncontrolledCollapse>

              <div className="offcanvas-main-links">
                <NavItem id="charts" className="mt-3">
                  <NavLink className="sub-menu-links" to="#">
                    <i className="fa fa-chart-line mr-1" />{" "}
                    <IntlMessages id="menu.charts" />
                    <div className="pr-3 float-right">
                      <i className="fa fa-angle-down color-light" />
                    </div>
                  </NavLink>
                </NavItem>
              </div>
              <UncontrolledCollapse
                toggler="#charts"
                className="list-style-none sub-menu-collapse"
              >
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/charts/apexcharts", true)
                  })}
                >
                  <NavLink to="/app/charts/apexcharts">
                    <i className="fa fa-chart-bar font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.apexcharts" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/charts/sparkline", true)
                  })}
                >
                  <NavLink to="/app/charts/sparkline">
                    <i className="fa fa-chart-line font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.sparkline" />
                  </NavLink>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/charts/chartjs", true)
                  })}
                >
                  <NavLink to="/app/charts/chartjs">
                    <i className="fa fa-chart-area font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.chartjs" />
                  </NavLink>
                </NavItem>
              </UncontrolledCollapse>

              <div className="offcanvas-main-links">
                <NavItem className="mt-3">
                  <NavLink className="sub-menu-links" to="/app/mails/emails">
                    <i className="far fa-edit mr-1" />{" "}
                    <IntlMessages id="menu.emails" />
                  </NavLink>
                </NavItem>
              </div>

              <div className="offcanvas-main-links">
                <NavItem className="mt-3">
                  <NavLink
                    className="sub-menu-links"
                    to="/app/editor/rich-editor"
                  >
                    <i className="far fa-envelope mr-1" />{" "}
                    <IntlMessages id="menu.editors" />
                  </NavLink>
                </NavItem>
              </div>

              <div className="offcanvas-main-links">
                <NavItem id="extras" className="mt-3">
                  <NavLink className="sub-menu-links" to="#">
                    <i className="far fa-file mr-1" />{" "}
                    <IntlMessages id="menu.extras" />
                    <div className="pr-3 float-right">
                      <i className="fa fa-angle-down color-light" />
                    </div>
                  </NavLink>
                </NavItem>
              </div>

              <UncontrolledCollapse
                toggler="#extras"
                className="list-style-none sub-menu-collapse"
              >
                <NavItem className="sub-menu-cus ms-2">
                  <a href="/login" target="_blank" rel="noreferrer">
                    <i className="fa fa-power-off font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.login" />
                  </a>
                </NavItem>
                <NavItem className="sub-menu-cus ms-2">
                  <a href="/register" target="_blank" rel="noreferrer">
                    <i className="fa fa-registered font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.register" />
                  </a>
                </NavItem>
                <NavItem className="sub-menu-cus ms-2">
                  <a href="/forgot-password" target="_blank" rel="noreferrer">
                    <i className="far fa-user font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.forgot.password" />
                  </a>
                </NavItem>
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/extras/products-page", true)
                  })}
                >
                  <a href="/app/extras/products-page">
                    <i className="far fa-user font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.products.page" />
                  </a>
                </NavItem>
              </UncontrolledCollapse>

              <div className="offcanvas-main-links">
                <NavItem id="dropdown" className="mt-3">
                  <NavLink className="sub-menu-links" to="#">
                    <i className="far fa-plus-square mr-1" />{" "}
                    <IntlMessages id="menu.menu" />
                    <div className="pr-3 float-right">
                      <i className="fa fa-angle-down color-light" />
                    </div>
                  </NavLink>
                </NavItem>
              </div>

              <UncontrolledCollapse
                toggler="#dropdown"
                className="list-style-none sub-menu-collapse"
              >
                <NavItem
                  className={classNames("sub-menu-cus ms-2", {
                    active: routeActive(pathname, "/app/blank/blankpage", true)
                  })}
                >
                  <a href="/app/blank/blankpage">
                    <i className="far fa-file-excel font-color-1 me-1" />{" "}
                    <IntlMessages id="menu.blank-page" />
                  </a>
                </NavItem>
              </UncontrolledCollapse>

              <div className="offcanvas-main-links">
                <NavItem className="mt-3">
                  <NavLink className="sub-menu-links" to="#">
                    <i className="far fa-flag me-1" />{" "}
                    <IntlMessages id="menu.no-dropdown" />
                  </NavLink>
                </NavItem>
              </div>
            </nav>
          </div>
        </PerfectScrollbar>
      </div>
    </div>
  );
}

export default Sidebar;
